/**
 * The conversation: bubbles, streaming status, chips and the error surface.
 *
 * Three traps live here, and each has already bitten a client of this graph:
 *
 * **(a) cumulative and delta events travel together.** reagent sends both
 * `messages` (deltas) and `messages/partial`/`complete` (the whole text so far).
 * Mixing them doubles the answer. The mode is decided PER MESSAGE ID by whichever
 * arrives first, and once a cumulative frame has been seen for an id its deltas
 * are ignored. (Measured on dev: this build sends only the cumulative shape —
 * but the graph decides that, not the transport, so both are handled.)
 *
 * **(b) `internal` is filtered by `messages/metadata`, not by chunk tags.** That
 * event arrives once per id, BEFORE the first chunk, and it is how the Telegram
 * bot drops the fact extractor's output. Chunk tags are read too, as a second
 * source.
 *
 * **(c) one bubble per message id, and the MID-TURN ONES STAY.** A bubble is
 * keyed on the id and an update replaces its text in place, because `finalize`
 * re-issues the answer under the SAME id and `add_messages` replaces it.
 *
 * **What a later message does NOT do is displace an earlier one.** This is a
 * deliberate reversal (user, 2026-09-01, from screenshots of text appearing and
 * then vanishing): the guide's mid-turn prose — what it found, what it is about
 * to do — is worth reading, and a turn that erases it as the final message
 * arrives looks like the app losing the answer. §0-collapsing bought one thing,
 * the duplicate below, and paid for it by deleting everything else the turn
 * said.
 *
 * **So the duplicate is closed narrowly instead.** Its one legal source is the
 * backend's REPLACE floor: an empty or too-short final message is served the
 * turn's own mid-turn prose under the final id, which puts the same words on
 * screen twice (dev, 2026-09-01, ids `…acdd` and `…0e0c`). A bubble whose
 * NORMALISED text — trimmed, whitespace collapsed, casefolded — exactly equals
 * one already visible in the same turn is therefore not drawn.
 *
 * **Exact equality, never containment.** «Проза, потом развёрнутый финал» is the
 * shape this reversal exists to keep, and containment eats it. The test is also
 * re-evaluated per update rather than latched: a bubble that grows PAST its twin
 * stops being a duplicate and comes back.
 *
 * A message with no text (only `tool_calls`) is not drawn at all.
 *
 * **(d) `reasoning` accumulates exactly like `content` does, per id.** Fixed
 * 2026-09-01 (reasoning-summary design doc §10.1/§10.9, R9) — a delta-mode
 * `messages` frame's `reasoning` is one chunk of the round's thinking, not
 * the whole thing so far, and assigning it (the previous behaviour) kept
 * only the last chunk. See the measurement in `applyMessage` below. This is
 * about the `?debug=1` raw-reasoning surface only (`Bubble.tsx`) — the
 * reasoning-summary BLOCK on the canvas board never reads this field at all,
 * by design (§10.3/§10.5): it reads sanitised `kind: "reasoning"` wire
 * frames through `activity/reasoningTypes.ts` instead.
 *
 * **(e) `peterhof_duo` speaks with ONE voice (2026-09-02).** The bridge is
 * joined by a VOICE — the small model's own final answer — and everything
 * the BIG model writes in between is `pa_internal` and must never draw a
 * bubble, whether it arrives live (a `pa_internal`/`langsmith:hidden` tag on
 * `message_meta`, folded into the same `books.internal` set §(b) already
 * maintains) or in a restored thread (`additional_kwargs.pa_internal` on the
 * message itself — the checkpoint carries no tags at all). A turn with the
 * voice on therefore shows exactly two guide bubbles: the bridge, then the
 * voice — never the big model's prose in between, whatever `pa_internal`
 * messages it wrote or how many. Board cards and chips are untouched; only
 * the TEXT the big model produced is hidden.
 *
 * **(f) `peterhof_orch`'s answer carries its turn's cards (2026-09-11).** Two
 * doors, one field (`Bubble.cards`): a `turn_cards` frame naming the answer's
 * id (live — the re-emitted answer never reaches `messages` again), and
 * `additional_kwargs.pa_turn_cards` on the message (restore, and a fresh-id
 * answer that does stream). A frame can land BEFORE its bubble, so it waits in
 * `books.pendingCards`; a bubble that never draws — (c)'s exact duplicate —
 * hands its cards to the turn's last answer at `endTurn()`, and `restore()`
 * hands a skipped twin's cards to the kept one, so the cards are never lost to
 * the dedup that hid their text.
 */

import { create } from 'zustand';
import { confirmedErase, reconcilesFinalAnswer, useSession } from './sessionStore';
import { readMessage, readTurnCards, type NormalizedEvent, type TurnCard, type WireMessage } from '../transport/events';

export type Bubble = {
  id: string;
  role: 'user' | 'guide';
  text: string;
  /** Filled while the message is still arriving. */
  streaming: boolean;
  reasoning: string | null;
  createdAt: number;
  turn: number;
  /**
   * A `peterhof_duo` bridge line — the small model's short line ahead of the
   * turn's real answer, its own message id, drawn as an ordinary bubble a
   * shade quieter (`Bubble.tsx`). Never true for `peterhof_canvas` or any
   * other graph; always `false` for a user bubble.
   */
  bridge: boolean;
  /**
   * A `peterhof_duo` voice line under voice mode — the small model's own
   * FINAL answer. Styled identically to any other guide bubble (only the
   * bridge is quieter); carried for future styling. Always `false` for a
   * user bubble and for every other graph.
   */
  voice: boolean;
  /**
   * `'progress'` = a `peterhof_orch` NARRATOR line: the helper reporting what it
   * just did while the job is still running. Always a `bridge` bubble too — both
   * are the guide talking mid-turn — and what the kind adds is that these lines
   * GROUP: `delegationId` says which `delegate` call they belong to and
   * `progressSeq` the order they were read in. `undefined` on the `say` handoff
   * and on every `peterhof_duo` bridge, which stand on their own.
   */
  bridgeKind?: string;
  delegationId?: string;
  progressSeq?: number;
  /**
   * `'nav'` = this line was a navigation EVENT, not something the visitor
   * typed, and it is drawn as a pill («📍 Я у точки «Монплезир»») rather than
   * as a bubble.
   *
   * **It does not survive an F5, and that is a decision** (design §4.3). What
   * `/state` returns is an ordinary user message: the label lives only in local
   * state, and restoring it would mean a second reader that guesses from the
   * Russian sentence what kind of message it was — the reader-keyed-on-prose
   * trap this repository avoids everywhere else. The phrases are written to
   * read fine as ordinary bubbles. If the backend ever marks such messages in
   * `additional_kwargs`, the honest restore has ONE place to go: `restore`.
   *
   * Orthogonal to `bridge`: that one is about who WROTE a guide line, this
   * one about what a visitor line IS.
   */
  kind?: 'nav';
  /**
   * The cards the turn drew, snapshotted onto its answer (`peterhof_orch`
   * only, §(f)). Absent on every other bubble and every other graph. Whether
   * they are DRAWN is `sessionStore.showsTurnCards`' decision, not this
   * store's.
   */
  cards?: TurnCard[];
  /**
   * A user bubble pushed by `App.tsx`'s `enqueueOrSend` while a turn was
   * still streaming (`chat/sendQueue.ts`) — the message is on screen but has
   * not actually been sent to the backend yet. Drawn a shade quieter
   * (`Bubble.tsx`, «ожидает…»). `markUserSent` clears it the moment the queue
   * is drained. Always `false`/absent on every other bubble.
   */
  pending?: boolean;
};

export type ChatError = {
  /** Neutral, visitor-facing. The exception name goes to the console only. */
  text: string;
  /** The utterance that failed, put back in the composer. */
  retryText?: string;
  retryAfterSeconds?: number;
};

export type ChatStatus = 'idle' | 'sending' | 'streaming' | 'reconnecting';

export type ChatState = {
  bubbles: Bubble[];
  status: ChatStatus;
  /** Live «Смотрю афишу музея…» lines, by tool name. */
  activity: string[];
  error: ChatError | null;
  runId: string | null;
  turn: number;
  /** Text put back into the composer after a failure. */
  draft: string;
  modelInUse: string | null;
  modelRequested: string | null;
  /** `run_meta.worker_model` — `peterhof_orch`/`peterhof_tandem` only, the
   *  model the DELEGATED work ran on. `null` on every other graph, which
   *  never sends the field, exactly like `modelInUse` before its first
   *  `run_meta`. */
  workerModelInUse: string | null;

  /**
   * Pushes the visitor's bubble. Returns its id, so a caller that pushes it
   * AHEAD of the actual send (`chat/sendQueue.ts`, `pending: true`) can later
   * restamp it — `markUserSent` — without pushing a second bubble for the
   * same message.
   */
  pushUser: (text: string, kind?: 'nav', pending?: boolean) => string;
  /**
   * Clears a queued bubble's pending mark and restamps its `turn` to the
   * CURRENT one — called right after `beginTurn()`, so the bubble joins the
   * turn it is actually answered in (`endTurn`'s pending-cards walk, and
   * `progressGroups.ts`, both read `Bubble.turn`). A no-op if the id is gone
   * (should not happen — nothing else removes a bubble by id).
   */
  markUserSent: (id: string) => void;
  ingest: (event: NormalizedEvent) => void;
  restore: (messages: unknown) => void;
  setStatus: (status: ChatStatus) => void;
  fail: (error: ChatError) => void;
  clearError: () => void;
  setDraft: (text: string) => void;
  beginTurn: (assistantId?: string) => void;
  endTurn: () => void;
  reset: () => void;
};

type Bookkeeping = {
  /** ids the graph marked internal — never drawn. */
  internal: Set<string>;
  /** Explicitly replaced answer ids; late stream frames must not redraw them. */
  replaced: Set<string>;
  /** #76: finalized values are authoritative over late message frames. */
  finalized: Set<string>;
  reconcileAnswer: boolean;
  answerOpen: boolean;
  answerAliases: Map<string, string>;
  answerDrafts: Set<string>;
  deduplicated: Map<string, string>;
  deferredAnswers: Map<string, Set<string>>;
  deferredFrames: Map<string, WireMessage>;
  toolMessages: Set<string>;
  /** ids that have shown a cumulative frame; their deltas are ignored. */
  cumulative: Set<string>;
  /** ids whose text was assembled from deltas. */
  deltas: Map<string, string>;
  /**
   * ids tagged `pa_bridge` on `messages/metadata`, which arrives BEFORE the
   * first chunk of that id — so by the time `applyMessage` sees the message,
   * the tag is already here to read. `message.bridge` (from
   * `additional_kwargs`, read once at `messages/complete` time) covers the
   * case where a build sends no separate metadata event at all; this set is
   * what makes the FIRST streamed chunk already know.
   */
  bridge: Set<string>;
  /** ids tagged `pa_voice` on `messages/metadata`, same shape as `bridge`
   *  above — the small model's final answer under voice mode. */
  voice: Set<string>;
  /**
   * ids tagged `pa_bridge_progress` on `messages/metadata`, with the delegation
   * and the sequence read off the two prefixed tags beside it.
   *
   * **Tags are the only live door for these three facts.** A narrator line is
   * streamed by the backend's relay, and the MESSAGE carrying its
   * `additional_kwargs` is one the platform's stream has already emitted under
   * that id — it dedupes, so the kwargs arrive only after an F5. Without this
   * the group would form on reload and never during the turn it is about.
   */
  progress: Map<string, { delegationId: string; seq: number }>;
  /** ids whose `?debug=1` reasoning text was assembled from deltas — see the
   *  measurement below `applyMessage`'s `reasoning` handling. */
  reasoningDeltas: Map<string, string>;
  /** `turn_cards` frames whose bubble has not been drawn yet, by message id
   *  (§(f)) — claimed when that bubble appears, or by the turn's last answer
   *  at `endTurn()`. */
  pendingCards: Map<string, TurnCard[]>;
};

const books: Bookkeeping = {
  internal: new Set(),
  replaced: new Set(),
  finalized: new Set(),
  reconcileAnswer: false,
  answerOpen: false,
  answerAliases: new Map(),
  answerDrafts: new Set(),
  deduplicated: new Map(),
  deferredAnswers: new Map(),
  deferredFrames: new Map(),
  toolMessages: new Set(),
  cumulative: new Set(),
  deltas: new Map(),
  bridge: new Set(),
  voice: new Set(),
  progress: new Map(),
  reasoningDeltas: new Map(),
  pendingCards: new Map(),
};

/** Fall back to the streamed accepted message when no closing values arrived. */
function flushDeferredAnswers(bubbles: Bubble[], turn: number): Bubble[] {
  let next = bubbles;
  for (const [id, rejected] of books.deferredAnswers) {
    next = next.filter(bubble => bubble.turn !== turn || bubble.role !== 'guide' || !rejected.has(bubble.id));
    for (const oldId of rejected) books.pendingCards.delete(oldId);
    const message = books.deferredFrames.get(id);
    if (message && !message.internal && !books.internal.has(id) && message.content && !next.some(bubble => bubble.id === id)) {
      const cards = message.turnCards ?? books.pendingCards.get(id);
      next = [...next, {
        id, role: 'guide', text: message.content, streaming: false,
        reasoning: message.reasoning, createdAt: Date.now(), turn,
        bridge: false, voice: false, ...(cards ? { cards } : {}),
      }];
      books.pendingCards.delete(id);
    }
  }
  books.deferredAnswers.clear();
  books.deferredFrames.clear();
  books.answerOpen = false;
  return next;
}

/**
 * The key the duplicate test compares on.
 *
 * A reader cannot see trailing space, a doubled newline or a capital, so
 * neither may the test: the backend's floor copies the prose through a
 * different code path and the two arrive equal in MEANING rather than in bytes.
 */
function sameText(value: string): string {
  return value.trim().replace(/\s+/g, ' ').toLowerCase();
}

/**
 * The key the CUT test compares on — `sameText` with list markers and
 * punctuation gone, because the backend's belt that cuts a questionnaire turn
 * to its first question also drops the «1. » in front of the kept question
 * (`peterhof_orch.conversation.first_question_only`), and a reader sees the
 * same words either way.
 */
function cutKey(value: string): string {
  return value
    .toLowerCase()
    .replace(/(^|\s)\d+[.)](?=\s)/g, ' ')
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim();
}

/** The shortest final message the cut rule may replace a bubble with. */
const MIN_CUT_CHARS = 20;

/**
 * `restore()`'s sort key within one turn segment (§(e)): the bridge stands
 * first, the voice stands last, and anything neither (an ordinary
 * `peterhof_canvas` answer, or a fallback final with no markers at all) keeps
 * the middle — which is where every non-duo turn already lived, so this is a
 * strict generalisation of the old bridge-only comparator, not a new rule for
 * graphs that never send either tag.
 */
function segmentRank(bubble: Bubble): number {
  if (bubble.bridge) return 0;
  if (bubble.voice) return 2;
  return 1;
}

// `pa_internal` joins the hidden set (2026-09-02, §(e)) — the big model's own
// prose on `peterhof_duo`, under voice mode. Redundant with `langsmith:hidden`
// today (the backend tags a hidden message with both), listed explicitly
// anyway: two independent readers of "is this internal" is what keeps this
// suppression working even if the backend ever ships one tag without the
// other.
const HIDDEN_TAGS = ['internal', 'langsmith:hidden', 'pa_internal'];
/** `peterhof_duo`'s own tags, distinct from the hidden ones — a bridge or
 * voice line is SHOWN (the bridge quieter, the voice ordinary), unlike
 * anything in `HIDDEN_TAGS`. */
const BRIDGE_TAG = 'pa_bridge';
const VOICE_TAG = 'pa_voice';
/** `peterhof_orch`'s narrator: the kind, plus the two prefixed tags carrying
 *  which delegation a line belongs to and where it stands in that job. */
const PROGRESS_TAG = 'pa_bridge_progress';
const PROGRESS_KIND = 'progress';
const DELEGATION_TAG_PREFIX = 'pa_delegation:';
const PROGRESS_SEQ_TAG_PREFIX = 'pa_progress_seq:';

const isObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

/** `books.reasoningDeltas` retains a message's whole `?debug=1` reasoning for
 *  the rest of the session (same shape `books.deltas` already has, §(d)) —
 *  and a thinking model's reasoning routinely outgrows its own answer. Capped
 *  rather than left unbounded for a surface almost nobody has open; past this
 *  many characters a `<details>` dump is unreadable anyway (review, !265). */
const MAX_REASONING_DELTA_CHARS = 20_000;

/*
 * `workerModelInUse` is a fact about THIS thread only, since 2026-09-16. It
 * used to be remembered in `localStorage` across reloads and «Новый диалог»
 * as «a fact about the deploy» — and one deploy runs several graphs with
 * DIFFERENT helpers, so the value learned on `peterhof_orch_harness`
 * (`GigaChat-3-Ultra`) followed the visitor onto `peterhof_orch` and the menu
 * named a helper that graph does not run. What a fresh conversation shows
 * before its first `run_meta` is now the deploy file's own answer, per graph
 * (`sessionStore.workerModelInUseFor` over `transport/api.deployModels`),
 * which is the fact that memory was standing in for.
 */

export const useChat = create<ChatState>((set, get) => ({
  bubbles: [],
  status: 'idle',
  activity: [],
  error: null,
  runId: null,
  turn: 0,
  draft: '',
  modelInUse: null,
  modelRequested: null,
  workerModelInUse: null,

  pushUser: (text, kind, pending) => {
    const { turn, bubbles } = get();
    const id = `user:${turn}:${bubbles.length}`;
    set((state) => ({
      bubbles: [
        ...state.bubbles,
        {
          id,
          role: 'user',
          text,
          streaming: false,
          reasoning: null,
          bridge: false,
          voice: false,
          createdAt: Date.now(),
          turn: state.turn,
          ...(kind ? { kind } : {}),
          ...(pending ? { pending: true } : {}),
        },
      ],
      // A turn that ends without chips is a complete turn; yesterday's buttons
      // must not linger over today's question (UI_CONTRACT §3).
          error: null,
    }));
    return id;
  },

  markUserSent: (id) =>
    set((state) => {
      const index = state.bubbles.findIndex((bubble) => bubble.id === id);
      if (index === -1) return state;
      const bubbles = state.bubbles.slice();
      bubbles[index] = { ...bubbles[index]!, pending: false, turn: state.turn };
      return { bubbles };
    }),

  /**
   * Rebuilds the conversation from `GET /threads/{id}/state` after a reload.
   *
   * The board came back and the conversation did not, which reads as a guide
   * that forgot the visitor — so both are restored. Only what was VISIBLE is:
   * tool messages are context for the model, and an AI message carrying nothing
   * but `tool_calls` never had a bubble in the first place.
   *
   * **Every AI message of a turn is drawn here too** — (c)'s rule read off the
   * transcript the checkpoint kept, so a reloaded thread shows what the live one
   * showed. The run boundary is the visitor: a turn begins with a human message,
   * which is all the segmentation `values.messages` carries and all it needs.
   * Inside one segment an exact repeat is skipped, and the FIRST occurrence is
   * the one kept — the floor's copy under the final id says nothing the prose it
   * copied did not.
   *
   * **A `peterhof_duo` bridge line is re-sorted ahead of the answer, and a
   * voice line ahead of NOTHING but the answer stays behind it — `segmentRank`
   * (§(e)).** Live the bridge always arrives first and the voice last (the
   * bridge is emitted before the answer starts, the voice is the answer), but
   * the checkpoint is a graph write with no such promise, and after an F5 the
   * visitor must see what they saw live. AI messages of one segment are
   * buffered and flushed through that rank, a STABLE sort so two ordinary
   * answers (neither bridge nor voice — every non-duo turn) keep the order
   * they arrived in.
   *
   * **A `pa_internal` message is skipped outright, never buffered.** It is the
   * big model's own prose on `peterhof_duo` under voice mode — it never had a
   * bubble live, so it gets none here either, whatever text it carries (a
   * message with real content, unlike the tool-call-only ones the empty-text
   * check below already drops).
   */
  restore: (messages) => {
    if (!Array.isArray(messages)) return;
    const bubbles: Bubble[] = [];
    /** Normalised texts already drawn for the turn in progress, and the bubble
     *  that drew each — a skipped twin's cards go to it (§(f)). */
    let seen = new Map<string, Bubble>();
    /** This turn's AI bubbles, held back so `segmentRank` can reorder them
     * before they join `bubbles`. */
    let segment: Bubble[] = [];

    const flushSegment = () => {
      // `Array#sort` is stable (spec-guaranteed since ES2019): two bubbles of
      // the same rank keep their arrival order.
      segment.sort((a, b) => segmentRank(a) - segmentRank(b));
      bubbles.push(...segment);
      segment = [];
    };

    for (const raw of messages) {
      if (!raw || typeof raw !== 'object') continue;
      const message = raw as Record<string, unknown>;
      const type = message.type;
      if (type !== 'human' && type !== 'ai') continue;
      const id =
        typeof message.id === 'string' ? message.id : `restored:${bubbles.length + segment.length}`;
      const content = typeof message.content === 'string' ? message.content : '';
      const kwargs = isObject(message.additional_kwargs) ? message.additional_kwargs : null;
      // §(e): checked before the empty-text skip below, because an internal
      // message can carry real prose — the big model's hidden answer — and
      // still must never become a bubble.
      if (type === 'ai' && kwargs?.pa_internal === true) continue;
      if (content.trim() === '') continue;
      if (type === 'human') {
        flushSegment(); // the previous turn's bubbles are final now
        seen = new Map<string, Bubble>();
        bubbles.push({
          id,
          role: 'user',
          text: content,
          streaming: false,
          reasoning: null,
          bridge: false,
          voice: false,
          createdAt: Date.now(),
          turn: 0,
        });
        continue;
      }
      const key = sameText(content);
      const cards = readTurnCards(kwargs?.pa_turn_cards);
      const twin = seen.get(key);
      if (twin) {
        if (cards && !twin.cards) twin.cards = cards;
        continue;
      }
      // §(g): the narrator's group is rebuilt from the message's own kwargs, the
      // only source a reloaded thread has — `messages/metadata` tags belong to a
      // run that is over. What a reload cannot recover is a line the turn was
      // interrupted before CHECKPOINTING, and that is accepted (docs/graphs/orch.md, the
      // UI contract's narrator note): the group comes back one line short rather
      // than being invented.
      const progress =
        kwargs?.pa_bridge_kind === PROGRESS_KIND
          ? {
              bridgeKind: PROGRESS_KIND,
              delegationId: typeof kwargs.pa_delegation_id === 'string' ? kwargs.pa_delegation_id : '',
              progressSeq: typeof kwargs.pa_progress_seq === 'number' ? kwargs.pa_progress_seq : 0,
            }
          : null;
      const bubble: Bubble = {
        id,
        role: 'guide',
        text: content,
        streaming: false,
        reasoning: null,
        bridge: kwargs?.pa_bridge === true,
        voice: kwargs?.pa_voice === true,
        createdAt: Date.now(),
        turn: 0,
        ...(cards ? { cards } : {}),
        ...(progress ?? {}),
      };
      seen.set(key, bubble);
      segment.push(bubble);
    }
    flushSegment();
    if (bubbles.length === 0) return;
    set({ bubbles });
  },

  ingest: (event) => {
    switch (event.kind) {
      case 'run_started':
        set({ runId: event.runId, status: 'streaming' });
        break;

      case 'message_meta': {
        if (event.tags.some((tag) => HIDDEN_TAGS.includes(tag))) books.internal.add(event.id);
        if (event.tags.includes(BRIDGE_TAG)) books.bridge.add(event.id);
        if (event.tags.includes(VOICE_TAG)) books.voice.add(event.id);
        if (event.tags.includes(PROGRESS_TAG)) {
          const tagged = (prefix: string) =>
            event.tags.find((tag) => tag.startsWith(prefix))?.slice(prefix.length) ?? '';
          const seq = Number.parseInt(tagged(PROGRESS_SEQ_TAG_PREFIX), 10);
          books.progress.set(event.id, {
            delegationId: tagged(DELEGATION_TAG_PREFIX),
            seq: Number.isFinite(seq) ? seq : 0,
          });
        }
        break;
      }

      case 'message': {
        applyMessage(event.message, event.cumulative);
        break;
      }

      case 'values': {
        // #193: a CONFIRMED erase gives this browser a new visitor key — before anything
        // else in this frame, and whatever the answer bookkeeping below decides.
        const erased = confirmedErase(event.values);
        if (erased) useSession.getState().acknowledgeErase(erased);
        if (!books.reconcileAnswer || typeof event.values.final_answer_id !== 'string') break;
        const id = event.values.final_answer_id;
        // before_agent clears this marker, including on turns that interrupt.
        // Requiring that boundary prevents the initial checkpoint from drawing
        // an old answer even when its bubble was not restored or deduped.
        if (!id) { books.answerOpen = true; break; }
        if (!books.answerOpen) break;
        if (!id || books.finalized.has(id) || !Array.isArray(event.values.messages)) break;
        const messages = event.values.messages.map(readMessage);
        const answer = messages.find((message) => message?.id === id);
        if (!answer || answer.type !== 'ai' || answer.toolCalls.length || answer.internal) break;
        set((state) => {
          if (state.status === 'idle') return state;
          const existing = state.bubbles.findIndex((bubble) => bubble.id === id);
          // Initial values may still contain the preceding turn's closing id.
          if (existing !== -1 && state.bubbles[existing]!.turn !== state.turn) return state;
          let index = existing;
          const rejectedIds = new Set([...books.deferredAnswers.values()].flatMap(ids => [...ids]));
          if (rejectedIds.size) {
            // A retry can already be visible when its replacement event lands.
            // Keep the first rejected bubble's position/key and remove every
            // rejected copy AND the streamed retry in this same state update.
            const rejected = state.bubbles.findIndex((bubble) => rejectedIds.has(bubble.id) && bubble.turn === state.turn);
            if (rejected !== -1) index = rejected;
          }
          const twinId = books.deduplicated.get(id);
          if (index === -1 && twinId) {
            index = state.bubbles.findIndex((bubble) =>
              bubble.id === twinId && bubble.turn === state.turn && sameText(bubble.text) === sameText(answer.content),
            );
          }
          if (index === -1) {
            const savedIds = new Set(messages.flatMap((message) => message ? [message.id] : []));
            for (let at = state.bubbles.length - 1; at >= 0; at -= 1) {
              const bubble = state.bubbles[at]!;
              if (bubble.turn !== state.turn) continue;
              // A draft discarded by a belt can have another id. Legitimate
              // mid-turn prose still present in state must keep its bubble.
              if (bubble.role === 'guide' && !bubble.bridge && books.answerDrafts.has(bubble.id) && !savedIds.has(bubble.id)) {
                index = at;
                break;
              }
            }
          }
          const previous = index === -1 ? null : state.bubbles[index]!;
          if (previous && previous.id !== id) books.replaced.add(previous.id);
          books.finalized.add(id);
          for (const rejectedId of rejectedIds) books.replaced.add(rejectedId);
          books.deferredAnswers.clear();
          books.deferredFrames.clear();
          books.answerOpen = false;
          if (previous) books.finalized.add(previous.id);
          if (previous) books.answerAliases.set(id, previous.id);
          const cards = answer.turnCards ?? books.pendingCards.get(id) ?? state.bubbles[existing]?.cards ?? previous?.cards;
          books.pendingCards.delete(id);
          for (const rejectedId of rejectedIds) books.pendingCards.delete(rejectedId);
          const bubble: Bubble = {
            ...(previous ?? {}),
            // Keep the React key, position and timestamp of the existing bubble.
            id: previous?.id ?? id,
            role: 'guide', text: answer.content, streaming: false,
            reasoning: answer.reasoning ?? previous?.reasoning ?? null,
            bridge: false, voice: false, turn: state.turn,
            createdAt: previous?.createdAt ?? Date.now(),
            ...(cards ? { cards } : {}),
          };
          if (index === -1) return { bubbles: [...state.bubbles.filter(each => !rejectedIds.has(each.id)), bubble] };
          const bubbles = state.bubbles.slice();
          bubbles[index] = bubble;
          return { bubbles: rejectedIds.size
            ? bubbles.filter((each, at) => at === index || (!rejectedIds.has(each.id) && each.id !== id))
            : bubbles };
        });
        break;
      }

      case 'run_meta': {
        // The header shows the model that ACTUALLY ran, not the one we asked
        // for: env can still override a per-run value that equals the dataclass
        // default, and only the graph knows which one won (§5.2).
        if (event.meta.model) set({ modelInUse: event.meta.model });
        // `worker_model` — `peterhof_orch`/`peterhof_tandem` only. Same
        // conditional-set as `model` above: absent means the graph never
        // sends it, not "the worker model is unknown", so a missing field
        // never clears an EARLIER value from the same run.
        if (event.meta.worker_model) set({ workerModelInUse: event.meta.worker_model });
        break;
      }

      case 'turn_cards': {
        // §(f): onto the bubble if it is on screen, otherwise held until it is.
        const { messageId, cards } = event;
        set((state) => {
          const bubbleId = books.answerAliases.get(messageId) ?? messageId;
          const index = state.bubbles.findIndex((bubble) => bubble.id === bubbleId);
          if (index === -1) {
            books.pendingCards.set(messageId, cards);
            return state;
          }
          const bubbles = state.bubbles.slice();
          bubbles[index] = { ...bubbles[index]!, cards };
          return { bubbles };
        });
        break;
      }

      /**
       * `peterhof_live`'s narrator, arriving as its own `dh_ui` frame rather
       * than as `additional_kwargs` on an ordinary message (`peterhof_orch`'s
       * shape) — the helper runs on the worker's own thread, so there is no
       * in-turn message to tag. Built into exactly the bubble a
       * `pa_bridge_kind=progress` message already is (`bridge: true`,
       * `bridgeKind: PROGRESS_KIND`, `delegationId`, `progressSeq`), so
       * `progressGroups.ts` groups it under the same `say` handoff and a
       * later report collapses the group unchanged.
       *
       * Keyed on `messageId` and upserted like a streamed message would be:
       * the same frame can arrive twice (once live on the worker's side
       * stream, once more if the report run replays it under the same id per
       * the wire contract), and a second arrival must update the bubble in
       * place rather than draw a duplicate.
       */
      case 'progress': {
        set((state) => {
          const index = state.bubbles.findIndex((bubble) => bubble.id === event.messageId);
          const bubble: Bubble = {
            id: event.messageId,
            role: 'guide',
            text: event.text,
            streaming: false,
            reasoning: null,
            bridge: true,
            voice: false,
            createdAt: index === -1 ? Date.now() : state.bubbles[index]!.createdAt,
            turn: state.turn,
            bridgeKind: PROGRESS_KIND,
            delegationId: event.delegationId,
            progressSeq: event.seq,
          };
          if (index === -1) return { bubbles: [...state.bubbles, bubble] };
          const bubbles = state.bubbles.slice();
          bubbles[index] = bubble;
          return { bubbles };
        });
        break;
      }

      case 'answer_replacement': {
        const { oldMessageId, newMessageId } = event;
        set((state) => {
          const oldIds = new Set([oldMessageId, ...(books.deferredAnswers.get(oldMessageId) ?? [])]);
          const oldIndex = state.bubbles.findIndex((bubble) => oldIds.has(bubble.id));
          const oldBubble = oldIndex === -1 ? null : state.bubbles[oldIndex]!;
          const newBubble = state.bubbles.find((bubble) => bubble.id === newMessageId);
          // The backend event is an instruction about one already visible
          // guide answer in this turn. Never let it hide a visitor bubble,
          // another turn's bubble, or an id that already belongs to a user.
          if (
            !oldBubble ||
            oldBubble.role !== 'guide' ||
            oldBubble.turn !== state.turn ||
            (newBubble && (newBubble.role !== 'guide' || newBubble.turn !== state.turn))
          ) {
            return state;
          }
          books.replaced.add(oldMessageId);
          if (books.reconcileAnswer && books.answerOpen && !books.toolMessages.has(newMessageId)) {
            // #184 already has an explicit replacement event. On a backend
            // that declared #76's values contract, keep the draft's position
            // until the corrected state arrives: no remove/add flicker.
            books.deferredAnswers.delete(oldMessageId);
            books.deferredFrames.delete(oldMessageId);
            books.deferredAnswers.set(newMessageId, new Set([
              ...(books.deferredAnswers.get(newMessageId) ?? []), ...oldIds,
            ]));
            return state;
          }
          for (const oldId of oldIds) books.pendingCards.delete(oldId);
          return { bubbles: state.bubbles.filter((bubble) => !oldIds.has(bubble.id)) };
        });
        break;
      }

      case 'run_error': {
        // eslint-disable-next-line no-console
        console.error('[run]', event.error, event.message);
        get().fail({ text: 'Гид не смог закончить ответ. Попробуем ещё раз?' });
        break;
      }

      default:
        break;
    }

    function applyMessage(message: WireMessage, cumulative: boolean): void {
      // §(e): `additional_kwargs.pa_internal` is the whole-message half of
      // "never render" — a build that skips the `messages/metadata` tag frame
      // still carries the flag on the message itself. Folded into the SAME
      // set the tag path writes, so this id is suppressed for the rest of the
      // turn regardless of which signal arrived (or arrives again later).
      if (message.internal) books.internal.add(message.id);
      if (books.internal.has(message.id) || books.replaced.has(message.id) || books.finalized.has(message.id)) return;
      if (message.type === 'tool' || message.type === 'system') {
        // A tool result is context for the model, not prose for the visitor.
        // What the visitor sees of it is the board and the activity line.
        return;
      }
      if (message.type === 'human') return; // our own turn, already on screen

      if (message.modelName) set({ modelInUse: message.modelName });

      const names = message.toolCalls.map((call) => call.name);
      if (names.length) {
        books.toolMessages.add(message.id);
        const rejected = books.deferredAnswers.get(message.id);
        if (rejected) {
          // A retry that calls a tool is an intermediate step, never the final answer.
          books.deferredAnswers.delete(message.id);
          books.deferredFrames.delete(message.id);
          for (const id of rejected) books.pendingCards.delete(id);
          set(state => ({ bubbles: state.bubbles.filter(bubble =>
            bubble.turn !== state.turn || bubble.role !== 'guide' || !rejected.has(bubble.id),
          ) }));
        }
        books.answerDrafts.delete(message.id);
      } else books.answerDrafts.add(message.id);
      if (names.length > 0) {
        set((state) => {
          const activity = state.activity.slice();
          for (const name of names) if (!activity.includes(name)) activity.push(name);
          return { activity };
        });
      }

      let text: string;
      let reasoning: string | null;
      if (cumulative) {
        books.cumulative.add(message.id);
        // ASSIGNED, never appended — this is the whole content so far, and
        // that is true of `reasoning` too: a cumulative frame already carries
        // everything the round has produced up to this point.
        text = message.content;
        reasoning = message.reasoning;
      } else {
        if (books.cumulative.has(message.id)) return; // (a): mode is per id
        text = (books.deltas.get(message.id) ?? '') + message.content;
        books.deltas.set(message.id, text);
        // R9 (design doc §10.1/§10.9): MEASURED, not assumed. `readContent` in
        // `transport/events.ts` reads `reasoning` per CHUNK — one wire message,
        // one delta — and `graphs/peterhof_agent/reasoning_replay.py`'s own
        // docstring confirms the mechanism: "LangChain auto-streams even
        // `ainvoke` while the app is being streamed" (its
        // `_convert_chunk_to_generation_chunk` override exists precisely
        // because that path — not the non-streaming one — is the one this
        // graph actually takes). So a delta-mode `messages` frame's
        // `reasoning` field is a FRAGMENT of the round's reasoning, exactly
        // like `content` is a fragment of the answer, and assigning it (as
        // this line used to) kept only the LAST chunk.
        // On dev the canvas graph was separately measured sending only the
        // cumulative shape for `messages` (see this file's header) — so this
        // branch is not exercised by that traffic today — but the transport
        // already treats `messages`-vs-`messages/partial` as the GRAPH's
        // choice, not a fixed property of the deployment (§(a) above), and
        // `reasoning` deserves the same treatment `text` already gets rather
        // than a value that goes quietly wrong the day a graph streams it.
        if (message.reasoning) {
          const prev = books.reasoningDeltas.get(message.id) ?? '';
          // Dropping the OLDEST characters (not truncating the newest) keeps
          // whatever the visitor is about to read next — the reasoning
          // that led to where the turn currently stands — intact.
          const next = (prev + message.reasoning).slice(-MAX_REASONING_DELTA_CHARS);
          books.reasoningDeltas.set(message.id, next);
        }
        reasoning = books.reasoningDeltas.get(message.id) ?? null;
      }

      const done = message.finishReason !== null;
      // Either half is enough: `message_meta`'s tag arrives before the first
      // chunk on a live stream, `additional_kwargs.pa_bridge` covers a build
      // that skips the metadata event. Sticky by construction — `books.bridge`
      // is never cleared for an id mid-turn, so a later chunk cannot un-flag it.
      const bridge = message.bridge || books.bridge.has(message.id);
      const voice = message.voice || books.voice.has(message.id);
      // Same two doors as `bridge`: the run TAG (live, before the first chunk)
      // or the message's own kwargs (a build that sends no metadata event, and
      // the restore path). A progress line is a bridge line too, so the tag
      // alone is enough to know it is quiet — this only adds the grouping.
      const tagged = books.progress.get(message.id);
      const progress =
        message.bridgeKind === PROGRESS_KIND
          ? { delegationId: message.delegationId ?? '', seq: message.progressSeq ?? 0 }
          : tagged;
      // §(f): the message's own snapshot, or a frame that arrived before it.
      const cards = message.turnCards ?? books.pendingCards.get(message.id) ?? null;
      if (books.deferredAnswers.has(message.id)) {
        books.deferredFrames.set(message.id, { ...message, content: text, reasoning });
        return;
      }

      set((state) => {
        const index = state.bubbles.findIndex((bubble) => bubble.id === message.id);
        // A message carrying only tool calls has no bubble of its own (c).
        if (index === -1 && text.trim() === '') return state;
        const bubble: Bubble = {
          id: message.id,
          role: 'guide',
          text,
          streaming: !done,
          reasoning,
          bridge,
          voice,
          createdAt: index === -1 ? Date.now() : state.bubbles[index]!.createdAt,
          turn: state.turn,
          ...(cards ? { cards } : {}),
          ...(progress
            ? {
                bridgeKind: PROGRESS_KIND,
                delegationId: progress.delegationId,
                progressSeq: progress.seq,
              }
            : {}),
        };
        // The mid-turn bubbles STAY; only an exact repeat of one already on
        // screen is withheld — the backend's floor serving this turn's own prose
        // again under the final message's id.
        const key = sameText(text);
        const duplicate =
          key !== '' ?
          state.bubbles.find(
            (each, at) =>
              at !== index &&
              each.role === 'guide' &&
              each.turn === state.turn &&
              sameText(each.text) === key,
          ) : undefined;
        if (duplicate) {
          if (books.reconcileAnswer) books.deduplicated.set(message.id, duplicate.id);
          // Not latched: a bubble that later grows past its twin is drawn again.
          return index === -1
            ? state
            : { bubbles: state.bubbles.filter((_, at) => at !== index) };
        }
        books.deduplicated.delete(message.id);
        if (cards) books.pendingCards.delete(message.id);
        // **A final message that is a CUT of a bubble already on screen replaces
        // it** (prod, 2026-09-16). The backend streams the model's own words
        // under the run's id and then publishes the belted answer under the
        // checkpoint's id — the same message, and when a belt SHORTENED it
        // (`first_question_only` cut three slot questions to one) the two ids
        // differ and the visitor read both. A shorter final whose words open
        // the earlier bubble can only be that bubble after a belt; it takes the
        // bubble's place. Containment the other way round (a fuller final) is
        // untouched — that is the shape §(c)'s reversal exists to keep.
        // `done` is deliberately NOT required: the published answer carries no
        // `finish_reason` (it is built by `finalize`, not by the model), so the
        // frame that matters here arrives with it null. The earlier bubble's own
        // stream being FINISHED is the guard instead — a final still growing
        // against a finished bubble it opens is a cut, not a twin.
        if (index === -1 && !bridge && !progress) {
          const cut = cutKey(text);
          if (cut.length >= MIN_CUT_CHARS) {
            const at = state.bubbles.findIndex((each) => {
              if (each.role !== 'guide' || each.turn !== state.turn || each.bridge || each.streaming) return false;
              const whole = cutKey(each.text);
              return whole.length > cut.length && whole.startsWith(cut);
            });
            if (at !== -1) {
              const bubbles = state.bubbles.slice();
              bubbles[at] = { ...bubbles[at]!, ...bubble, createdAt: bubbles[at]!.createdAt };
              return { bubbles };
            }
          }
        }
        if (index === -1) return { bubbles: [...state.bubbles, bubble] };
        const bubbles = state.bubbles.slice();
        bubbles[index] = { ...bubbles[index]!, ...bubble };
        return { bubbles };
      });
    }
  },

  setStatus: (status) => set({ status }),

  fail: (error) =>
    set((state) => ({
      error,
      status: 'idle',
      activity: [],
      bubbles: flushDeferredAnswers(state.bubbles, state.turn).map((bubble) =>
        bubble.streaming ? { ...bubble, streaming: false } : bubble,
      ),
    })),

  clearError: () => set({ error: null }),
  setDraft: (draft) => set({ draft }),

  beginTurn: (assistantId) => {
    books.reconcileAnswer = reconcilesFinalAnswer(assistantId ?? '');
    books.answerOpen = false;
    set((state) => ({
      turn: state.turn + 1,
      activity: [],
      status: 'sending',
      error: null,
      runId: null,
    }));
  },

  endTurn: () =>
    set((state) => {
      const bubbles = flushDeferredAnswers(state.bubbles, state.turn).map((bubble) =>
        bubble.streaming ? { ...bubble, streaming: false } : bubble,
      );
      // §(f): cards whose bubble never drew — the answer was (c)'s exact
      // duplicate of a mid-turn bubble — go under the turn's last answer, the
      // text the visitor read as it. Dropped only when the turn drew none.
      if (books.pendingCards.size > 0) {
        const cards = [...books.pendingCards.values()].pop()!;
        books.pendingCards.clear();
        for (let at = bubbles.length - 1; at >= 0; at -= 1) {
          const bubble = bubbles[at]!;
          if (bubble.turn !== state.turn) break;
          if (bubble.role !== 'guide' || bubble.bridge) continue;
          if (!bubble.cards) bubbles[at] = { ...bubble, cards };
          break;
        }
      }
      return { status: 'idle', activity: [], bubbles };
    }),

  reset: () => {
    books.internal.clear();
    books.replaced.clear();
    books.finalized.clear();
    books.reconcileAnswer = false;
    books.answerOpen = false;
    books.answerAliases.clear();
    books.answerDrafts.clear();
    books.deduplicated.clear();
    books.deferredAnswers.clear();
    books.deferredFrames.clear();
    books.toolMessages.clear();
    books.cumulative.clear();
    books.deltas.clear();
    books.bridge.clear();
    books.voice.clear();
    books.progress.clear();
    books.reasoningDeltas.clear();
    books.pendingCards.clear();
    set({
      bubbles: [],
      status: 'idle',
      activity: [],
          error: null,
      runId: null,
      turn: 0,
      draft: '',
      modelInUse: null,
      // A new thread has not heard which model the helper runs yet; the deploy
      // file answers for it until its first `run_meta` (see the note above).
      workerModelInUse: null,
    });
  },
}));

/** Exposed for the unit tests, which drive the reducer without a server. */
export const __books = books;
